import { Component } from '@angular/core';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { forkJoin } from 'rxjs';

import {
  TeamWithMembersDto,
  Team,
  CategoryDto,
  Language,
  SupporterEditDto,
  SupporterDto } from '../../shared/model';
import { TeamsViewService } from './teams-view.service';
import { SpinnerService } from '../../shared/spinner/spinner.service';
import { MainViewService } from '../../main-view/main-view.service';
import { SharedService } from '../../shared/shared.service';
import { TranslatedNotificationService } from '../../shared/translation/translated-notification.service';

import { EditTeamComponent } from './edit-team/edit-team.component';
import { EditSupporterComponent } from './edit-supporter/edit-supporter.component';

@Component({
  selector: 'app-teams-view',
  templateUrl: './teams-view.component.html',
  styleUrls: [
    './teams-view.component.css',
    '../admin-grid.css',
    '../../shared/styles/small-navigation.css',
    '../../shared/styles/filters.css',
    '../../shared/styles/confirm-dialog.css',
    '../../shared/styles/translation.css']
})
export class TeamsViewComponent {

  allCollapsed = false;
  searchInput: string;
  teams: Team[];
  filteredTeamsWithMembers: TeamWithMembersDto[];
  teamsWithNoMatch: Team[];
  teamsWithMembers: TeamWithMembersDto[];
  emptyTeams: Team[];
  emptyTeamsFiltered: Team[];
  collapsedTeams: any[] = [];
  categories: CategoryDto[] = [];
  languages: Language[] = [];
  technicians: SupporterDto[] = [];
  supporters: SupporterEditDto[] = [];
  deleteOpened = false;
  teamToDelete: string;
  supporterToDelete: string;
  techniciansLoaded = false;

  private filteredSupporters: SupporterEditDto [] = [];
  private allSupporters: SupporterEditDto [] = [];

  constructor(private teamsService: TeamsViewService,
    private notificationService: TranslatedNotificationService,
    private spinner: SpinnerService,
    private modalService: NgbModal,
    private mainService: MainViewService,
    private sharedService: SharedService) {
    this.fetchData();
    this.fetchTechnicians();
  }

  fetchData() {
    this.spinner.show('spinner');
    return forkJoin(
      this.teamsService.getTeams(),
      this.teamsService.getTeamsWithMembers(),
      this.mainService.getAdminCategories(),
      this.sharedService.getLanguagesTranslations())
        .subscribe(([teams, teamsWithMembers, categories]) => {
          this.teams = teams;
          this.teamsWithMembers = teamsWithMembers;
          this.filteredTeamsWithMembers = this.teamsWithMembers;
          this.categories = this.sharedService.translateCategories(categories);
          this.languages = this.sharedService.getTranslatedLanguages();
          this.getEmptyTeams();
          this.createExpandedTeams();
          this.allSupporters = this.getAllSupporters();
          this.spinner.hide('spinner');
        },
        error => {
          this.spinner.hide('spinner');
          this.notificationService.showMsgError('error.loading_teams_n_members', 'Error while loading teams and members.');
        });
  }

  fetchTechnicians() {
    this.teamsService.getTechnicians()
      .subscribe(res => {
        this.technicians = res;
        this.techniciansLoaded = true;
      },
      (err) => {
        this.notificationService.showMsgError('error.loading_technicians', 'Error while loading technicians.');
      });
  }

  fetchTeams() {
    this.teamsService.getTeams()
      .subscribe(res => {
        this.teams = res;
        this.getEmptyTeams();
        this.createExpandedTeams();
        this.spinner.hide('spinner');
      },
      (err) => {
        this.spinner.hide('spinner');
        this.notificationService.showMsgError('error.loading_teams', 'Error while loading teams.');
      });
  }

  fetchTeamMembers() {
    this.teamsService.getTeamsWithMembers()
      .subscribe(res => {
        this.teamsWithMembers = res;
        this.filteredTeamsWithMembers = [...this.teamsWithMembers];
        this.getEmptyTeams();
        this.allSupporters = this.getAllSupporters();
        this.displayNoSupportersMatchText();
        this.spinner.hide('spinner');
      },
      (err) => {
        this.spinner.hide('spinner');
        this.notificationService.showMsgError('error.getting_team_members', 'Error while loading team members');
      });
  }

  getTeamProperty(teamId: string, prop: string): string {
    if (this.teams) {
      return this.teams.find(t => t.Id === teamId)[prop];
    }

    return '';
  }

  openTeamFormModal() {
    const modalRef = this.modalService.open(EditTeamComponent);

    modalRef.result.then((result) => {
      this.spinner.show('spinner');
      const team = this.getTeamFromResult(result);
      this.teamsService.addTeam(team)
        .subscribe(res => res,
        (err) => {
          this.spinner.hide('spinner');
          this.notificationService.showMsgError('error.add_team', 'Error while adding team');
        },
        () => {
          this.fetchTeams();
        });
    }).catch(error => error);
  }

  getTeamFromResult(form: any): Team {
    return <Team> {
      Name: form.Name,
      Location: form.Location
    };
  }

  openEditTeamModal(teamId: string) {
    const modalRef = this.modalService.open(EditTeamComponent);
    const teamToEdit = this.teams.find(t => t.Id === teamId);
    modalRef.componentInstance.team = teamToEdit;
    modalRef.componentInstance.setFormValues(teamToEdit);

    modalRef.result.then((result) => {
      this.spinner.show('spinner');
      const team = <Team>result;
      this.teamsService.editTeam(team)
        .subscribe(res => res,
        (err) => {
          this.spinner.hide('spinner');
          this.notificationService.showMsgError('error.add_team', 'Error while adding team');
        },
        () => {
          this.fetchTeams();
        });
    }).catch(error => error);
  }

  createExpandedTeams() {
    this.collapsedTeams = [];
    this.teams.forEach(t => {
      this.collapsedTeams.push({
        id: t.Id,
        collapsed: false
      });
    });
  }

  teamCollapsed(teamId: string) {
    if (this.collapsedTeams && this.collapsedTeams.length > 0) {
      const team = this.collapsedTeams.find(c => c.id === teamId);
      return team !== undefined ? team.collapsed : true;
    }
    return true;
  }

  collapseTeam(elem: any) {
    const team = this.collapsedTeams.find(t => t.id === elem.srcElement.id);
    team.collapsed = !team.collapsed;

    this.setAllCollapsed();
  }

  setAllCollapsed() {
    const areAllCollapsed = (this.collapsedTeams.findIndex(t => t.collapsed === false) === -1);
    this.allCollapsed = areAllCollapsed;
  }

  collapseAll() {
    this.setCollapseForAllElements(true);
    this.setAllCollapsed();
  }

  expandAll() {
    this.setCollapseForAllElements(false);
    this.setAllCollapsed();
  }

  setCollapseForAllElements(collapse: boolean) {
    this.collapsedTeams.forEach(e => {
      e.collapsed = collapse;
    });
  }

  getSupporterCategories(categories: string[]): string {
    const supporterCategories = this.categories.filter(c => categories.indexOf(c.Id) !== -1);

    return supporterCategories.map(category => category.Name).join(', ').toString();
  }

  getSupporterLanguages(languages: string[]): string {
    const supporterLanguages = this.languages.filter(c => languages.indexOf(c.Id) !== -1);

    return supporterLanguages.map(lang => lang.Name).join(', ').toString();
  }

  openUserFormModal() {
    const modalRef = this.modalService.open(EditSupporterComponent);
    this.provideCollectionDataToUserForm(modalRef);

    modalRef.componentInstance.technicians = this.getTechniciansToAdd();
    modalRef.componentInstance.initializeForm();

    modalRef.result.then((result) => {
      this.spinner.show('spinner');
      const supporter = this.getSupporterFromModal(result);
      this.teamsService.addSupporter(supporter)
        .subscribe(res => res,
        (err) => {
          this.notificationService.showExceptionError(err.error);
          this.spinner.hide('spinner');
        },
        () => {
          this.fetchTeamMembers();
        });
    }).catch(error => error);
  }

  openEditUserFormModal(supporterId: string) {
    const modalRef = this.modalService.open(EditSupporterComponent);
    this.provideCollectionDataToUserForm(modalRef);
    modalRef.componentInstance.supporter = this.getSupporterToEdit(supporterId);
    modalRef.componentInstance.initializeForm();

    modalRef.result.then((result) => {
      this.spinner.show('spinner');
      const supporter = this.getSupporterFromModal(result, supporterId);
      this.teamsService.editSupporter(supporter)
        .subscribe(res => {
          this.sendNotification(supporterId);
        },
        (err) => {
          this.notificationService.showExceptionError(err.error);
          this.spinner.hide('spinner');
        },
        () => {
          this.fetchTeamMembers();
        });
    }).catch(error => error);
  }

  getSupporterFromModal(form: any, id?: string): SupporterEditDto {
    const technician = form && form.Technician ? form.Technician : null;
    const team = form && form.Team ? form.Team : null;

    const emailRaw = technician && technician.Email ? technician.Email : technician;
    const teamIdRaw = team && team.Id ? team.Id : team;
    const categories = Array.isArray(form && form.Categories)
      ? form.Categories.map(x => (x || '').toString()).filter(x => !!x)
      : [];
    const languages = Array.isArray(form && form.Languages)
      ? form.Languages.map(x => (x || '').toString()).filter(x => !!x)
      : [];

    const email = (emailRaw || '').toString();
    const teamId = (teamIdRaw || '').toString();

    const supporter = <SupporterEditDto>{
      Email: email,
      FirstName: ((form && form.FirstName) || '').toString(),
      LastName: ((form && form.LastName) || '').toString(),
      CategoryIds: categories,
      LanguageIds: languages,
      TeamId: teamId
    };

    if (id) {
      supporter.Id = id;
    }

    return supporter;
  }

  getTechniciansToAdd(): SupporterDto[] {
    return this.technicians.filter(t => this.allSupporters.findIndex(s => s.Email === t.Email) === -1);
  }

  filter(input: string) {
    if (input === undefined || input === null || input.trim().length ===  0) {
      this.filteredSupporters = [];
    }

    this.filteredSupporters = this.allSupporters.filter(s => s.Email.indexOf(input.toLowerCase().trim()) !== -1);
    this.searchInput = input;
  }

  deleteClick(id: string, deletingSupporter: boolean) {
    if (deletingSupporter) {
      this.supporterToDelete = id;
    } else {
      this.teamToDelete = id;
    }

    this.deleteOpened = true;
  }

  deleteClose() {
    this.deleteOpened = false;
    this.teamToDelete = null;
    this.supporterToDelete = null;
  }

  deleteConfirmed() {
    if (this.supporterToDelete) {
      this.spinner.show('spinner');
      this.deleteSupporter();
    }

    if (this.teamToDelete) {
      this.spinner.show('spinner');
      this.deleteTeam();
    }

    this.deleteClose();
  }

  isFilteredOut(supporterId: string) {
    if (this.filteredSupporters.length === 0) {
      return this.searchInput === '' || this.searchInput === undefined;
    }

    return this.filteredSupporters.findIndex(fs => fs.Id === supporterId) >= 0;
  }

  areTechniciansLoaded(): boolean {
    return this.techniciansLoaded;
  }

  handleFilteredRecords(filteredResults: TeamWithMembersDto[]) {
    if (Array.isArray(filteredResults)) {
      this.filteredTeamsWithMembers = this.getNotEmptyFilteredTeams(filteredResults);
      this.emptyTeamsFiltered = this.getEmptyFilteredTeams(filteredResults);
      this.displayNoSupportersMatchText();
    }
  }

  allSupportersDontMatch(teamId: string) {
    if (!!this.teamsWithNoMatch && this.teamsWithNoMatch.length > 0) {
      return this.teamsWithNoMatch.find(t => t.Id === teamId) !== undefined;
    }

    return false;
  }

  private getAllSupporters(): SupporterEditDto[] {
    const supporters = [];
    this.teamsWithMembers.forEach(c => {
      supporters.push(c.Supporters);
    });

    return [].concat.apply([], supporters);
  }

  private provideCollectionDataToUserForm(modalRef: any) {
    modalRef.componentInstance.categories = this.categories;
    modalRef.componentInstance.languages = this.languages;
    modalRef.componentInstance.teams = this.teams;
  }

  private getSupporterToEdit(id: string) {
    return this.allSupporters.find(s => s.Id === id);
  }

  private getEmptyTeams() {
    if (this.teams.length > this.teamsWithMembers.length) {
      this.emptyTeams = this.teams.filter(t => this.teamsWithMembers.findIndex(tm => tm.TeamId === t.Id) === -1);
      this.emptyTeamsFiltered = [...this.emptyTeams];
    } else {
      this.emptyTeams = [];
      this.emptyTeamsFiltered = [];
    }
  }

  private deleteSupporter() {
    this.teamsService.deleteSupporter(this.supporterToDelete)
    .subscribe(() => {
      this.fetchTeamMembers();
    },
    (err) => {
      this.spinner.hide('spinner');
      this.notificationService.showExceptionError(err.error);
    });
  }

  private deleteTeam() {
    this.teamsService.deleteTeam(this.teamToDelete)
    .subscribe(() => {
      this.fetchTeamMembers();
      this.fetchTeams();
    },
    (err) => {
      this.spinner.hide('spinner');
      this.notificationService.showExceptionError(err.error);
    });
  }

  private sendNotification(supporterId: string) {
    this.teamsService.notifySupporter(supporterId)
    .subscribe(() => {
      this.notificationService.showMsgSuccess('success.save_profile', 'Profile of supporter was saved successfully');
    },
    err => {
      this.notificationService.showMsgInfo(
        'info.notification_not_sent',
        'Changes are saved, but server could not send notification e-mail about changes in supporter profile.');
    });
  }

  private getNotEmptyFilteredTeams(filteredResults: TeamWithMembersDto[]): TeamWithMembersDto[] {
    return filteredResults.filter(r => this.emptyTeams.find(e => e.Id === r.TeamId) === undefined);
  }

  private getEmptyFilteredTeams(filteredResults: TeamWithMembersDto[]): Team[] {
    return this.emptyTeams.filter(t => filteredResults.find(tm => tm.TeamId === t.Id) !== undefined);
  }

  private displayNoSupportersMatchText() {
    this.teamsWithNoMatch = this.teams
    .filter(t =>
      this.emptyTeams.find(e => e.Id === t.Id) === undefined
      && this.filteredTeamsWithMembers.find(ft => ft.TeamId === t.Id) !== undefined
      && this.filteredTeamsWithMembers.find(ft => ft.TeamId === t.Id).Supporters.length === 0);
  }
}
